import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button, Select } from "./primitives";

export const PAGE_SIZES = [10, 25, 50] as const;

/**
 * Pager for a list already filtered in the browser: "11–20 of 83", page
 * size, previous and next. Page is 1-based and clamped by the caller.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPage,
  onPageSize,
  noun = "items",
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
  noun?: string;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
      <span className="text-muted-foreground">
        {total === 0 ? `No ${noun}` : `${from}–${to} of ${total.toLocaleString()} ${noun}`}
      </span>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-muted-foreground">
          Per page
          <Select
            aria-label="Rows per page"
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Select>
        </label>
        <Button
          variant="quiet"
          size="sm"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="min-w-16 text-center text-muted-foreground">
          {page} / {pages}
        </span>
        <Button
          variant="quiet"
          size="sm"
          aria-label="Next page"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

/** The slice of `rows` for a page, with the page clamped to what exists. */
export function pageOf<T>(rows: T[], page: number, pageSize: number): { rows: T[]; page: number } {
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safe = Math.min(Math.max(1, page), pages);
  return { rows: rows.slice((safe - 1) * pageSize, safe * pageSize), page: safe };
}
